import { useState, type FormEvent } from 'react';
import { motion } from 'framer-motion';
import { Lock, Eye, EyeOff, AlertCircle } from 'lucide-react';
import { useProposal } from '../../context';

export function PasswordProtection() {
  const { verifyAndLoad } = useProposal();
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [error, setError] = useState('');
  const [isLoading, setIsLoading] = useState(false);

  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault();
    setError('');
    setIsLoading(true);

    const success = await verifyAndLoad(password);

    if (!success) {
      setError('Incorrect password. Please try again.');
      setPassword('');
    }

    setIsLoading(false);
  };

  return (
    <div className="min-h-screen bg-surface-dark flex items-center justify-center p-4">
      {/* Background Pattern */}
      <div className="absolute inset-0 overflow-hidden">
        <div
          className="absolute inset-0 opacity-5"
          style={{
            backgroundImage: `
              linear-gradient(rgba(237, 44, 44, 0.3) 1px, transparent 1px),
              linear-gradient(90deg, rgba(237, 44, 44, 0.3) 1px, transparent 1px)
            `,
            backgroundSize: '60px 60px',
          }}
        />
      </div>

      <motion.div
        className="relative w-full max-w-md"
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5 }}
      >
        {/* Card */}
        <div className="bg-white rounded-2xl shadow-2xl overflow-hidden">
          {/* Header */}
          <div className="bg-hul-navy px-8 py-10 text-center relative overflow-hidden">
            {/* Decorative Corner */}
            <div className="absolute top-0 right-0 w-20 h-20 bg-hul-red transform translate-x-10 -translate-y-10 rotate-45" />

            {/* Logo */}
            <div className="flex justify-center mb-4">
              <div className="relative w-16 h-16">
                <div className="absolute inset-0 bg-hul-red transform rotate-45" />
                <div className="absolute inset-1 bg-hul-navy transform rotate-45" />
                <div className="absolute inset-2 bg-hul-red transform rotate-45 flex items-center justify-center">
                  <span className="font-display text-white text-lg transform -rotate-45">
                    HUL
                  </span>
                </div>
              </div>
            </div>

            <h1 className="font-display text-2xl text-white tracking-wide mb-2">
              PROTECTED PROPOSAL
            </h1>
            <p className="text-white/70 text-sm">
              This proposal is password protected
            </p>
          </div>

          {/* Form */}
          <form onSubmit={handleSubmit} className="p-8">
            <div className="mb-6">
              <label
                htmlFor="password"
                className="block text-sm font-medium text-gray-700 mb-2"
              >
                Enter Password
              </label>
              <div className="relative">
                <div className="absolute inset-y-0 left-0 pl-4 flex items-center pointer-events-none">
                  <Lock className="w-5 h-5 text-gray-400" />
                </div>
                <input
                  type={showPassword ? 'text' : 'password'}
                  id="password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  className="w-full pl-12 pr-12 py-3 border-2 border-gray-200 rounded-lg focus:border-hul-red focus:ring-0 transition-colors font-mono"
                  placeholder="Enter password"
                  autoFocus
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className="absolute inset-y-0 right-0 pr-4 flex items-center text-gray-400 hover:text-gray-600"
                >
                  {showPassword ? (
                    <EyeOff className="w-5 h-5" />
                  ) : (
                    <Eye className="w-5 h-5" />
                  )}
                </button>
              </div>
            </div>

            {/* Error Message */}
            {error && (
              <motion.div
                className="mb-6 flex items-center gap-2 text-red-600 text-sm bg-red-50 px-4 py-3 rounded-lg"
                initial={{ opacity: 0, y: -10 }}
                animate={{ opacity: 1, y: 0 }}
              >
                <AlertCircle className="w-4 h-4 flex-shrink-0" />
                {error}
              </motion.div>
            )}

            {/* Submit Button */}
            <button
              type="submit"
              disabled={!password || isLoading}
              className="w-full btn-industrial disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {isLoading ? (
                <span className="flex items-center justify-center gap-2">
                  <svg className="animate-spin w-5 h-5" viewBox="0 0 24 24">
                    <circle
                      className="opacity-25"
                      cx="12"
                      cy="12"
                      r="10"
                      stroke="currentColor"
                      strokeWidth="4"
                      fill="none"
                    />
                    <path
                      className="opacity-75"
                      fill="currentColor"
                      d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                    />
                  </svg>
                  Verifying...
                </span>
              ) : (
                'View Proposal'
              )}
            </button>

            {/* Help Text */}
            <p className="mt-6 text-center text-sm text-gray-500">
              Contact your Herc-U-Lift representative if you
              <br />
              don't have the password.
            </p>
          </form>
        </div>

        {/* Company Name */}
        <div className="mt-6 text-center">
          <span className="text-white/40 text-sm font-mono">
            Herc-U-Lift Equipment Solutions
          </span>
        </div>
      </motion.div>
    </div>
  );
}
